import { useState, useCallback } from 'react';

export const useServiceCardHover = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleMouseEnter = useCallback((cardId) => {
    setHoveredCard(cardId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredCard(null);
  }, []);

  const getCardStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      background: isHovered 
        ? 'linear-gradient(135deg, #FFFFFF 0%, #E7EDF1 100%)' 
        : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
      border: isHovered ? '2px solid #0084CF' : '2px solid #E7EDF1',
      borderRadius: '20px',
      padding: '40px 30px',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      boxShadow: isHovered 
        ? '0 20px 40px rgba(0, 132, 207, 0.15), 0 8px 16px rgba(0, 132, 207, 0.1)' 
        : '0 4px 20px rgba(0, 132, 207, 0.08)',
      transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
      height: '380px',
      display: 'flex',
      flexDirection: 'column'
    };
  }, [hoveredCard]);

  const getBackgroundPatternStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      position: 'absolute',
      top: '-50%',
      right: '-50%',
      width: '200%',
      height: '200%',
      background: 'radial-gradient(circle, rgba(0, 132, 207, 0.06) 0%, transparent 70%)',
      transition: 'all 0.4s ease',
      opacity: isHovered ? 1 : 0,
      transform: isHovered ? 'scale(1.1)' : 'scale(1)',
    };
  }, [hoveredCard]);

  const getAccentLineStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #0084CF 0%, #7F9CC8 50%, #A9C4E1 100%)',
      transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
      transition: 'transform 0.4s ease',
    };
  }, [hoveredCard]);

  const getIconStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '70px',
      height: '70px',
      background: isHovered 
        ? 'linear-gradient(135deg, #0073B3 0%, #0084CF 100%)' 
        : 'linear-gradient(135deg, #0084CF 0%, #7F9CC8 100%)',
      borderRadius: '18px',
      marginBottom: '25px',
      boxShadow: isHovered 
        ? '0 12px 30px rgba(0, 132, 207, 0.3)' 
        : '0 8px 25px rgba(0, 132, 207, 0.2)',
      transition: 'all 0.4s ease',
      transform: isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
      zIndex: 2
    };
  }, [hoveredCard]);

  const getTitleStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      fontSize: '1.5rem',
      marginBottom: '20px',
      color: isHovered ? '#0084CF' : '#2C3E50',
      fontWeight: '300',
      lineHeight: '1.3',
      transition: 'color 0.3s ease',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxHeight: '2.6rem',
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    };
  }, [hoveredCard]);

  const getTextStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      fontSize: '1rem',
      lineHeight: '1.7',
      color: isHovered ? '#54626F' : '#54626F',
      marginBottom: '25px',
      flex: 1,
      transition: 'color 0.3s ease',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 4,
      WebkitBoxOrient: 'vertical',
      textOverflow: 'ellipsis',
      maxHeight: '6.8rem', // 4 lines * 1.7 line-height
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      fontWeight: '100',
    };
  }, [hoveredCard]);

  const getReadMoreStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.95rem',
      color: isHovered ? '#0073B3' : '#0084CF',
      textDecoration: 'none',
      fontWeight: '500',
      padding: '12px 20px',
      background: isHovered ? 'rgba(0, 132, 207, 0.15)' : 'rgba(0, 132, 207, 0.1)',
      borderRadius: '25px',
      transition: 'all 0.3s ease',
      border: isHovered ? '1px solid rgba(0, 132, 207, 0.3)' : '1px solid rgba(0, 132, 207, 0.2)',
      transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      boxShadow: isHovered ? '0 4px 12px rgba(0, 132, 207, 0.2)' : 'none',
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    };
  }, [hoveredCard]);

  const getArrowStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      marginLeft: '5px',
      transition: 'transform 0.3s ease',
      transform: isHovered ? 'translateX(3px)' : 'translateX(0)',
    };
  }, [hoveredCard]);

  const getHoverIconStyles = useCallback((cardId) => {
    const isHovered = hoveredCard === cardId;
    
    return {
      position: 'absolute',
      bottom: '20px',
      right: '20px',
      width: '40px',
      height: '40px',
      background: isHovered ? 'rgba(0, 132, 207, 0.15)' : 'rgba(0, 132, 207, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.4s ease',
      opacity: isHovered ? 1 : 0,
      transform: isHovered ? 'translateY(0) scale(1.1)' : 'translateY(10px) scale(1)',
      zIndex: 2
    };
  }, [hoveredCard]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    getCardStyles,
    getBackgroundPatternStyles,
    getAccentLineStyles,
    getIconStyles,
    getTitleStyles,
    getTextStyles,
    getReadMoreStyles,
    getArrowStyles,
    getHoverIconStyles,
  };
};
